import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Magnetic, EASE } from "@/components/site/motion";
import { PROFILE_PHOTO } from "@/lib/constants";
import { motion as fm } from "framer-motion";

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, -70]);
  const yText = useTransform(scrollY, [0, 700], [0, 40]);

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-32 pb-16" data-testid="hero-section">
      {/* ambient background */}
      <div className="bg-grid absolute inset-0 opacity-60" />
      <div className="absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-gradient-to-tr from-neutral-500 to-stone-300 opacity-[0.13] blur-3xl" />
      <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-gradient-to-tr from-stone-400 to-neutral-600 opacity-[0.09] blur-3xl" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#050505] to-transparent" />

      <div className="relative z-10 mx-auto grid w-[92%] max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <fm.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="eyebrow mb-7"
            data-testid="hero-eyebrow"
          >
            Portfolio 2026 — Working Worldwide
          </fm.p>

          <h1 data-testid="hero-headline">
            <span className="block overflow-hidden pb-[0.05em] -mb-[0.05em]">
              <fm.span
                className="font-display block text-[clamp(4.5rem,13vw,10.5rem)] font-bold leading-[0.9] tracking-tight text-white"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.15, ease: EASE }}
              >
                JARE
              </fm.span>
            </span>
            {["Versatile", "Designer"].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <fm.span
                  className="font-display block text-[clamp(1.9rem,4.6vw,3.9rem)] font-medium leading-[1.05] tracking-tight text-neutral-400"
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.3 + i * 0.13, ease: EASE }}
                >
                  {line}
                </fm.span>
              </span>
            ))}
          </h1>

          <fm.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: EASE }}
            className="mt-10 lg:hidden"
          >
            <div className="glass relative rounded-[2rem] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <div className="overflow-hidden rounded-[1.6rem]">
                <img
                  src={PROFILE_PHOTO}
                  alt="JARE — Versatile Creative Designer"
                  className="aspect-[4/5] w-full object-cover grayscale contrast-110"
                  data-testid="hero-profile-photo-mobile"
                />
              </div>
            </div>
          </fm.div>

          <fm.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: EASE }}
            className="mt-7 font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500 md:text-xs"
            data-testid="hero-disciplines"
          >
            Graphic Design · Motion · Video · UI Design · Web Development
          </fm.p>

          <fm.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg"
          >
            I work across disciplines — identity, motion, film and digital product —
            building visual worlds where design systems, cinematic craft and
            intelligent, machine-assisted workflows meet. One eye, many mediums.
          </fm.p>

          <fm.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Magnetic>
              <Link to="/work" className="btn-primary" data-testid="hero-cta-work">
                View Selected Work <ArrowRight className="h-4 w-4" />
              </Link>
            </Magnetic>
            <Magnetic>
              <Link to="/contact" className="btn-ghost" data-testid="hero-cta-contact">
                Start a Project
              </Link>
            </Magnetic>
          </fm.div>
        </motion.div>

        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: EASE }}
          className="relative mx-auto hidden w-full max-w-sm lg:col-span-5 lg:block lg:max-w-none"
        >
          <div className="glass relative rounded-[2rem] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
            <div className="overflow-hidden rounded-[1.6rem]">
              <img
                src={PROFILE_PHOTO}
                alt="JARE — Versatile Creative Designer"
                className="aspect-[4/5] w-full object-cover grayscale contrast-110"
                data-testid="hero-profile-photo"
              />
            </div>
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <span className="rounded-full border border-white/15 bg-black/50 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80 backdrop-blur-xl">
                JARE — 2026
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
